import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';

import { cn } from '../../lib/utils';

/**
 * Coarse-pointer tap target (44px), reserved as real layout — never bled.
 *
 * Same contract as `Switch`, with every number different because the geometry is.
 * The visible box is 16x16, so unlike the 44-wide switch it falls short on BOTH
 * axes and the reservation has to be all-round:
 *
 *  - `touch:m-3.5` reserves 14px on every side, growing the checkbox's own layout
 *    box to 44x44. A `DataTable` cell (`px-4 py-1`) simply gets a taller row and a
 *    wider select column on touch devices.
 *  - `touch:after:-inset-[15px]` grows the hit area to fill exactly that
 *    reservation, and no further.
 *
 * The two numbers differ by design, for the reason they differ on `Switch`: an
 * absolutely-positioned `::after` is offset from its container's PADDING box, and
 * this root carries a 1px `border`, so 15px of inset lands 14px outside the border
 * box — exactly the reserved margin, for a 16 + 14 + 14 = 44px target. Matching
 * them naively (`-inset-3.5`) silently yields 42px; changing the box size or the
 * border width without changing the inset breaks it the same way.
 *
 * An overlay WITHOUT the matching margin is the other trap: it spills past a
 * `py-1` cell into the neighbouring row, so a tap near a row boundary selects the
 * wrong record — worse than a tap that does nothing.
 * `__tests__/checkbox.test.tsx` derives the target size from all three numbers and
 * goes red if any of them drifts.
 *
 * Corollary for callers: never resize or `scale-*` a Checkbox — a transform
 * shrinks the reserved box along with the visible one and puts the target back
 * under 44px.
 */
const CHECKBOX_TOUCH_TARGET =
  "touch:m-3.5 touch:after:absolute touch:after:-inset-[15px] touch:after:content-['']";

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      'peer group relative h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground',
      CHECKBOX_TOUCH_TARGET,
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn('flex items-center justify-center text-current')}
    >
      {/*
        Radix renders the indicator for `checked` AND `checked="indeterminate"`, so
        the primitive owns the icon for both — a partial selection is a dash, not a
        tick. Both icons ship and `data-state` on the root picks one, rather than
        branching on the `checked` PROP: an uncontrolled checkbox
        (`defaultChecked="indeterminate"`) moves to a real checked state on the
        first click while the prop keeps its initial value, so a prop branch would
        leave it drawing a dash forever.
      */}
      <Minus className="h-3 w-3 group-data-[state=checked]:hidden" />
      <Check className="h-4 w-4 group-data-[state=indeterminate]:hidden" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
